import { useRef, useState } from 'react';
import { X, Plus } from 'lucide-react';
import { TODO_COLORS, TODO_COLOR_ORDER, type TodoColorKey } from '@/lib/todoColors';
import type { TodoCategory } from '@/hooks/useTodos';

interface Props {
  categories: TodoCategory[];
  activeCategoryId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string, hasTasks: boolean) => void;
  onRename: (id: string, name: string) => void;
  onAdd: () => void;
}

export function CategoryTabs({ categories, activeCategoryId, onSelect, onDelete, onRename, onAdd }: Props) {
  const [renamingId, setRenamingId]   = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  function startRename(cat: TodoCategory, e: React.MouseEvent) {
    e.stopPropagation();
    setRenamingId(cat.id);
    setRenameValue(cat.name);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function commitRename(id: string) {
    if (renameValue.trim()) onRename(id, renameValue.trim());
    setRenamingId(null);
  }

  return (
    <div
      style={{ display: 'flex', alignItems: 'flex-end', overflow: 'hidden', width: '100%' }}
      onPointerDownCapture={(e) => e.stopPropagation()}
    >
      {categories.map((cat) => {
        const isActive = cat.id === activeCategoryId;
        const colors   = TODO_COLORS[cat.color] ?? TODO_COLORS.yellow;
        return (
          <div
            key={cat.id}
            onClick={() => onSelect(cat.id)}
            onDoubleClick={(e) => startRename(cat, e)}
            title={cat.name}
            style={{
              flex: '0 1 86px', minWidth: 0,
              display: 'flex', alignItems: 'center', gap: 2,
              padding: '4px 5px 4px 7px',
              borderRadius: '4px 4px 0 0',
              cursor: 'pointer',
              backgroundColor: colors.bg,
              color: colors.ink,
              opacity: isActive ? 1 : 0.62,
              transform: isActive ? 'none' : 'translateY(2px)',
              transition: 'all .15s',
              userSelect: 'none',
              fontSize: 11,
              fontWeight: isActive ? 600 : 500,
            }}
          >
            {renamingId === cat.id ? (
              <input
                ref={inputRef}
                value={renameValue}
                onChange={(e) => setRenameValue(e.target.value)}
                onBlur={() => commitRename(cat.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')  commitRename(cat.id);
                  if (e.key === 'Escape') setRenamingId(null);
                }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  flex: 1, minWidth: 0, background: 'transparent',
                  border: 'none', outline: 'none',
                  color: 'inherit', fontSize: 'inherit', fontWeight: 'inherit',
                }}
              />
            ) : (
              <span style={{
                flex: 1, minWidth: 0,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {cat.name}
              </span>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(cat.id, cat.tasks.length > 0); }}
              style={{
                flexShrink: 0, background: 'none', border: 'none',
                cursor: 'pointer', padding: '1px 0',
                color: 'inherit', opacity: 0.65,
                display: 'flex', alignItems: 'center',
              }}
              title="Fechar categoria"
            >
              <X size={10} />
            </button>
          </div>
        );
      })}

      {/* Botão fixo para nova categoria */}
      <button
        onClick={(e) => { e.stopPropagation(); onAdd(); }}
        title="Nova categoria"
        style={{
          flexShrink: 0,
          background: 'rgba(0,0,0,0.08)', border: 'none',
          cursor: 'pointer', padding: '5px 7px',
          borderRadius: '4px 4px 0 0',
          color: '#555',
          display: 'flex', alignItems: 'center',
        }}
      >
        <Plus size={12} />
      </button>
    </div>
  );
}

export function getNextColor(categories: TodoCategory[]): TodoColorKey {
  const usedColors = new Set(categories.map((c) => c.color));
  const idx = categories.length % TODO_COLOR_ORDER.length;
  return TODO_COLOR_ORDER.find((k) => !usedColors.has(k)) ?? TODO_COLOR_ORDER[idx] ?? 'yellow';
}
